import { applicationPortalDialog as fdy } from "./foundry-parts.generated";
/**
 * @fileoverview AppPortalSearchHeader — the search bar that forms the dialog's
 * full-width top header. The dialog's close control sits at the end of this bar;
 * there is no separate title header.
 *
 * THE MARKUP IS FOUNDRY'S, NOT BLUEPRINT'S DEFAULT. `InputGroup`'s `leftIcon`
 * and `rightElement` props put the magnifier and the Filters button INSIDE
 * `bp6-input-group`, the latter wrapped in `bp6-input-action`. Foundry does
 * neither: both are SIBLINGS of the input group inside a flex wrapper of its
 * own, and the filter button sits in a `bp6-popover-target`.
 *
 * That is not cosmetic. The wrapper is the flex row that lays the header out,
 * so collapsing it moved the alignment onto a Blueprint element that does not
 * carry those rules — and every child below shifted one level in the tree,
 * which is why the parity report was comparing our input against Foundry's
 * icon and reporting the values of both as wrong.
 *
 * Composed from the capture rather than from the props that look equivalent:
 *
 *   div  __header__          the bar
 *     div  __input__         flex row
 *       span bp6-icon        magnifier, OUTSIDE the input group
 *       div  bp6-input-group bp6-large __input-group__
 *         input  __search-bar__
 *       span bp6-popover-target
 *         button  Filters
 *     button                 close
 */

import { Button, Icon, InputGroup } from "@blueprintjs/core";

interface AppPortalSearchHeaderProps {
  query: string;
  onQueryChange: (query: string) => void;
  onClose: () => void;
  onFiltersClick?: () => void;
  placeholder?: string;
  className?: string;
}

export function AppPortalSearchHeader({
  query,
  onQueryChange,
  onClose,
  onFiltersClick,
  placeholder = "Search for applications…",
  className,
}: AppPortalSearchHeaderProps) {
  return (
    <div
      data-testid="app-portal-search-header"
      className={[fdy["header@3wj5e8"], "bp-app-portal-search-header", className]
        .filter(Boolean)
        .join(" ")}
      data-protonpass-form=""
    >
      <div className={fdy["input"]}>
        {/*
          size 20, not Blueprint's default 16. The capture's glyph is the large
          one — the parity walk caught it as a different `d` on the <path>, which
          is the only way a wrong icon SIZE shows up when the shape is right.

          AND AN EXPLICIT COLOUR. Foundry's magnifier is `<svg fill="#5F6B7C">`,
          set as an attribute on the element; ours had no `color`, so Blueprint's
          `fill="currentColor"` inherited the dialog's own rgb(246,247,249) and
          the icon rendered near-white — the brightest thing in the header,
          against a muted grey upstream. Confirmed against the live tenant, whose
          computed fill is rgb(95, 107, 124).
        */}
        <Icon icon="search" size={20} color="var(--bp-palette-gray-1)" />
        <InputGroup
          data-testid="app-portal-search-input"
          /*
            `large`, because upstream the field is `bp6-input-group bp6-large`.
            The header takes its height from this control, so the size is
            load-bearing rather than stylistic.
          */
          large
          className={fdy["input-group"]}
          inputClassName={fdy["search-bar"]}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={placeholder}
          aria-label="You are currently in a modal application search area. To exit, click anywhere outside of the section."
        />
        {/*
          `bp6-popover-target` with no Popover. Upstream the Filters control
          opens one; here it is a plain handler, but the span is what the flex
          row spaces against, so the element stays.
        */}
        <span className="bp6-popover-target">
          <Button
            data-testid="app-portal-search-filters"
            small
            minimal
            icon="filter"
            text="Filters"
            onClick={onFiltersClick}
          />
        </span>
      </div>
      <Button
        data-testid="app-portal-search-close"
        small
        minimal
        icon="cross"
        aria-label="Close"
        onClick={onClose}
      />
    </div>
  );
}
